<template>
  <header 
    class="w-full fixed top-0 left-0 z-50 transition-all duration-500"
    :class="scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white/80 backdrop-blur-md'"
  >
    <nav class="w-11/12 mx-auto h-20 flex justify-between items-center">
      <!-- LOGO -->
      <router-link to="/" class="flex items-center group">
        <img 
          src="/images/mainlogo.svg" 
          alt="Kline" 
          class="h-10 md:h-12 w-auto transition-transform duration-300 group-hover:scale-105"
        />
      </router-link>

      <!-- MENU DESKTOP -->
      <div class="hidden lg:flex items-center space-x-1 xl:space-x-2">
        <a 
          v-for="item in menuItems" 
          :key="item.id"
          :href="item.href" 
          class="relative px-3 py-2 text-sm font-medium text-[#385674] hover:text-[#009B9D] transition-colors duration-300 group"
          @click="closeMenu"
        >
          {{ $t(item.label) }}
          <span class="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#009B9D] transition-all duration-300 group-hover:w-3/4"></span>
        </a>

        <!-- Language Switcher Desktop -->
        <div class="relative ml-2">
          <button 
            @click="toggleLangMenu"
            class="flex items-center space-x-2 px-3 py-2 rounded-full border border-[#009B9D]/30 hover:border-[#009B9D] transition-all duration-300 group"
          >
            <svg class="w-4 h-4 text-[#009B9D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
            </svg>
            <span class="text-sm font-medium text-[#385674] group-hover:text-[#009B9D] transition-colors">
              {{ currentLangLabel }}
            </span>
            <svg 
              class="w-3 h-3 text-[#385674] transition-transform duration-300"
              :class="langMenuOpen ? 'rotate-180' : ''"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <!-- Dropdown Language Menu -->
          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 scale-95 -translate-y-2"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 -translate-y-2"
          >
            <div 
              v-if="langMenuOpen"
              class="absolute top-full right-0 mt-2 w-36 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <button 
                v-for="lang in languages"
                :key="lang.code"
                @click="setLanguage(lang.code)"
                class="w-full flex items-center space-x-3 px-4 py-3 text-sm transition-colors duration-200"
                :class="currentLang === lang.code ? 'bg-[#009B9D]/10 text-[#009B9D] font-medium' : 'text-gray-700 hover:bg-gray-50'"
              >
                <span class="text-lg">{{ lang.flag }}</span>
                <span>{{ lang.name }}</span>
                <svg 
                  v-if="currentLang === lang.code" 
                  class="w-4 h-4 ml-auto text-[#009B9D]" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
              </button>
            </div>
          </transition>
        </div>

        <!-- CTA Button -->
        <a
          href="#"
          @click.prevent="openWhatsAppDemo"
          class="ml-3 bg-[#009B9D] text-white px-5 py-2.5 rounded-lg font-medium text-sm shadow-lg shadow-[#009B9D]/25 hover:shadow-xl hover:shadow-[#009B9D]/30 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
        >
          {{ $t('nav.demo') }}
        </a>
      </div>

      <!-- Mobile Menu Button & Language -->
      <div class="flex items-center space-x-3 lg:hidden">
        <!-- Mobile Language Toggle -->
        <button 
          @click="toggleLangMenu"
          class="flex items-center justify-center w-10 h-10 rounded-full border border-[#009B9D]/30 text-[#009B9D]"
        >
          <span class="text-lg">{{ currentLang === 'fr' ? '🇫🇷' : '🇬🇧' }}</span>
        </button>

        <!-- Hamburger Button -->
        <button 
          @click="toggleMenu" 
          class="relative w-10 h-10 flex items-center justify-center focus:outline-none"
          aria-label="Toggle menu"
        >
          <div class="relative w-6 h-5">
            <span 
              class="absolute left-0 w-full h-0.5 bg-[#385674] transition-all duration-300"
              :class="menuOpen ? 'top-2 rotate-45' : 'top-0'"
            ></span>
            <span 
              class="absolute left-0 top-2 w-full h-0.5 bg-[#385674] transition-all duration-300"
              :class="menuOpen ? 'opacity-0 scale-0' : 'opacity-100'"
            ></span>
            <span 
              class="absolute left-0 w-full h-0.5 bg-[#385674] transition-all duration-300"
              :class="menuOpen ? 'top-2 -rotate-45' : 'top-4'"
            ></span>
          </div>
        </button>
      </div>
    </nav>

    <!-- Mobile Language Dropdown -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div 
        v-if="langMenuOpen && !menuOpen"
        class="lg:hidden absolute top-20 right-4 w-36 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
      >
        <button 
          v-for="lang in languages"
          :key="lang.code"
          @click="setLanguage(lang.code)"
          class="w-full flex items-center space-x-3 px-4 py-3 text-sm transition-colors duration-200"
          :class="currentLang === lang.code ? 'bg-[#009B9D]/10 text-[#009B9D] font-medium' : 'text-gray-700 hover:bg-gray-50'"
        >
          <span class="text-lg">{{ lang.flag }}</span>
          <span>{{ lang.name }}</span>
        </button>
      </div>
    </transition>

    <!-- MOBILE MENU -->
    <transition
      enter-active-class="transition duration-400 ease-out"
      enter-from-class="opacity-0 -translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-300 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-4"
    >
      <div 
        v-if="menuOpen"
        class="lg:hidden bg-white/98 backdrop-blur-lg shadow-2xl absolute top-20 left-0 w-full"
      >
        <div class="flex flex-col items-center py-6 space-y-1">
          <a 
            v-for="(item, index) in menuItems" 
            :key="item.id"
            :href="item.href" 
            class="w-full text-center py-3 text-[#385674] font-medium hover:text-[#009B9D] hover:bg-[#009B9D]/5 transition-all duration-300"
            :style="{ animationDelay: `${index * 50}ms` }"
            @click="closeMenu"
          >
            {{ $t(item.label) }}
          </a>
          
          <!-- Mobile Language Selection -->
          <div class="w-full px-6 py-3">
            <div class="flex justify-center space-x-3">
              <button 
                v-for="lang in languages"
                :key="lang.code"
                @click="setLanguage(lang.code)"
                class="flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300"
                :class="currentLang === lang.code ? 'bg-[#009B9D] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                <span>{{ lang.flag }}</span>
                <span class="text-sm font-medium">{{ lang.code.toUpperCase() }}</span>
              </button>
            </div>
          </div>

          <a
            href="#"
            class="mt-3 bg-[#009B9D] text-white px-8 py-3 rounded-lg font-semibold text-sm shadow-lg shadow-[#009B9D]/25 cursor-pointer"
            @click.prevent="openWhatsAppDemo(); closeMenu()"
          >
            {{ $t('nav.demo') }}
          </a>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { ref, computed, onMounted, onUnmounted } from 'vue'

export default {
  name: 'NavbarComponent',
  setup() {
    const { locale } = useI18n()
    const menuOpen = ref(false)
    const langMenuOpen = ref(false)
    const scrolled = ref(false)
    const currentLang = ref(locale.value)

    const languages = [
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
      { code: 'en', name: 'English', flag: '🇬🇧' }
    ]

    const menuItems = [
      { id: 'home', href: '#', label: 'nav.home' },
      { id: 'features', href: '#features', label: 'nav.features' },
      { id: 'pricing', href: '#pricing', label: 'nav.pricing' },
      { id: 'how-it-works', href: '#how-it-works', label: 'nav.howItWorks' },
      { id: 'testimonials', href: '#testimonials', label: 'nav.testimonials' },
      { id: 'contact', href: '#contact', label: 'nav.contact' }
    ]

    const currentLangLabel = computed(() => {
      const lang = languages.find(l => l.code === currentLang.value)
      return lang ? lang.name : 'Français'
    })

    const toggleMenu = () => {
      menuOpen.value = !menuOpen.value
      if (menuOpen.value) langMenuOpen.value = false
    }

    const closeMenu = () => {
      menuOpen.value = false
      langMenuOpen.value = false
    }

    const toggleLangMenu = () => {
      langMenuOpen.value = !langMenuOpen.value
    }

    const setLanguage = (lang) => {
      currentLang.value = lang
      locale.value = lang
      localStorage.setItem('kline-locale', lang)
      langMenuOpen.value = false
    }

    const handleScroll = () => {
      scrolled.value = window.scrollY > 20
    }

    const openWhatsAppDemo = () => {
      const message = encodeURIComponent('Bonjour, je souhaite demander une démo de Kline.')
      window.open(`https://wa.me/237695496806?text=${message}`, '_blank')
    }

    const handleClickOutside = (e) => {
      if (langMenuOpen.value && !e.target.closest('.relative')) {
        langMenuOpen.value = false
      }
    }

    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
      document.addEventListener('click', handleClickOutside)
      // Restore saved language
      const savedLang = localStorage.getItem('kline-locale')
      if (savedLang) {
        currentLang.value = savedLang
        locale.value = savedLang
      }
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      menuOpen,
      langMenuOpen,
      scrolled,
      currentLang,
      currentLangLabel,
      languages,
      menuItems,
      toggleMenu,
      closeMenu,
      toggleLangMenu,
      setLanguage,
      openWhatsAppDemo
    }
  }
}
</script>

<style scoped>
/* Smooth scroll for anchor links */
html {
  scroll-behavior: smooth;
}
</style>